import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

const GoogleRecaptcha = ({
  res,
  error,
  submit,
  action,
  siteKeyV3,
  siteKeyV2,
  bypassRecaptcha,
  className,
  setToken,
  v2Error,
  v2ErrorClass,
}) => {
  const [url] = useState(
    `https://www.google.com/recaptcha/api.js?render=${siteKeyV3}`,
  );

  const defaultV2 = sessionStorage.getItem('defaultV2');

  const [showV2Error, setShowV2Error] = useState(false);
  const [v2Rendered, setV2Rendered] = useState(false);
  const [failedV3, setFailedV3] = useState(!!defaultV2);

  const userClickedV2 = () =>
    window.grecaptcha && window.grecaptcha.getResponse()?.length !== 0;

  const onV2UserSubmit = () => {
    if (userClickedV2()) {
      setShowV2Error(false);
    }
  };

  // Render v2 recaptcha component
  const renderV2Recaptcha = () => {
    // recaptcha functions must be defined in the global scope
    window.onV2UserSubmit = onV2UserSubmit;

    window.grecaptcha.ready(() => {
      setV2Rendered(true);
      window.grecaptcha.render(document.querySelector('#google_recaptcha'), {
        sitekey: siteKeyV2,
      });
    });
  };

  useEffect(() => {
    // reset needed to generate a new token for each request success or failure
    if (res && failedV3) {
      if (!res?.length) {
        // only hide recaptcha on successful res
        setFailedV3(false);
        sessionStorage.setItem('defaultV2', true);
      }
      // this function only exists on v2 so no need to specify id
      window.grecaptcha.reset();
    }
  }, [res]);

  useEffect(() => {
    // recaptcha fails, call never made, show v2
    if (error?.error.status === 400) {
      if (!error.error.data.error) {
        setFailedV3(true);
      }
    }
  }, [error]);

  useEffect(() => {
    const scripts = document.getElementsByTagName('script');
    const check = Array.from(scripts).every(script => script.src !== url);

    // if recaptcha script not already present in the DOM, add
    if (check) {
      const script = document.createElement('script');
      script.src = url;
      document.head.appendChild(script);
      // if defaultV2 is true check new recaptcha script tag has loaded then render v2 immediately
      if (defaultV2) {
        script.onload = () => {
          renderV2Recaptcha();
        };
      }
    } else if (failedV3) {
      // render v2 if showV2 is true
      if (!v2Rendered) {
        renderV2Recaptcha();
        window.grecaptcha.ready(() => {
          setV2Rendered(true);
          if (!bypassRecaptcha) {
            window.grecaptcha.render(
              document.querySelector('#google_recaptcha'),
              {
                sitekey: siteKeyV2,
              },
            );
          }
        });
      }
    }
  }, [failedV3]);

  useEffect(() => {
    if (submit && !failedV3) {
      // run v3
      window.grecaptcha.execute(siteKeyV3, { action }).then(token => {
        setToken(token);
      });
    } else if (submit && userClickedV2()) {
      // clear error and run v2
      const token = window.grecaptcha.getResponse();
      setToken(token);
    } else if (submit) {
      setShowV2Error(true);
    }
  }, [submit]);

  return (
    <>
      <div
        id="google_recaptcha"
        data-callback={failedV3 ? 'onV2UserSubmit' : ''}
        className={`${className || ''} ${failedV3 ? 'block' : 'd-none'}`}
      />
      {showV2Error && <p className={v2ErrorClass || ''}>{v2Error}</p>}
    </>
  );
};

GoogleRecaptcha.propTypes = {
  res: PropTypes.object,
  error: PropTypes.object,
  submit: PropTypes.bool.isRequired,
  action: PropTypes.string.isRequired,
  siteKeyV3: PropTypes.string.isRequired,
  siteKeyV2: PropTypes.string.isRequired,
  bypassRecaptcha: PropTypes.string,
  className: PropTypes.string,
  setToken: PropTypes.func.isRequired,
  v2Error: PropTypes.string.isRequired,
  v2ErrorClass: PropTypes.string,
};

export default GoogleRecaptcha;
